import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { IDiamondFilter } from 'types/diamondFilter.types'
import cn from 'clsx'
import s from './Pagination.module.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
} from '@fortawesome/free-solid-svg-icons'
import { ProductVariant } from '@commerce/types/product'
import { ISettingFilter } from 'types/settingFilter.types'
import { Taxon } from '@commerce/types/taxon'
import axios from 'axios'
import { IYadavProductVariants } from '@framework/types'

const Pagination = ({
  top,
  setLoading,
  setProductVariants,
  settingTotalPages,
  isSetting,
  taxon,
  settingFilterState,
  diamondFilterState,
  onPageUpdate,
  diamondMeta,
}: {
  top: number
  setLoading?: Dispatch<SetStateAction<boolean>>
  setProductVariants?: Dispatch<SetStateAction<ProductVariant[] | null>>
  settingTotalPages?: number
  isSetting?: boolean
  taxon?: Taxon
  settingFilterState?: ISettingFilter
  diamondFilterState?: IDiamondFilter
  onPageUpdate?: (value: number) => void
  diamondMeta?: number
}) => {
  const [totalPages, setTotalPages] = useState<number>(
    isSetting ? settingTotalPages ?? 0 : diamondMeta ?? 0
  )
  const [pageNumberLimit, setPageNumberLimit] = useState(5)
  const [maxPageNumberLimit, setMaxPageNumberLimit] = useState(5)
  const [minPageNumberLimit, setMinPageNumberLimit] = useState(0)

  const [currentPage, setCurrentPage] = useState(() => {
    if (diamondFilterState?.page !== undefined) {
      return diamondFilterState.page
    } else if (isSetting) {
      return 1
    } else {
      return 0
    }
  })

  // TODO: implement action types in SettingFilterProvider to handle page requests.
  useEffect(() => {
    if (isSetting && settingFilterState) {
      if (setLoading) {
        setLoading(true)
      }
      const taxonFilter =
        settingFilterState.style.length === 0
          ? [taxon && taxon.id]
          : settingFilterState.style
      axios
        .get<IYadavProductVariants>(`/api/spree/v1/product/variants`, {
          params: {
            taxon: taxonFilter.toString(),
            metals: settingFilterState.metal.toString(),
            price: settingFilterState.price.toString(),
            sort: settingFilterState.sort.toString(),
            page: currentPage,
            images_size: '240x240',
          },
        })
        .then((res) => {
          if (setProductVariants) {
            setProductVariants(res.data.data)
            if (setLoading) {
              setLoading(false)
            }
          }
        })
    } else {
      if (onPageUpdate) {
        onPageUpdate(currentPage)
      }
    }
    if (setLoading) {
      setLoading(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, onPageUpdate])
  useEffect(() => {
    if (settingFilterState) {
      setCurrentPage(1)
    }
  }, [settingFilterState])
  useEffect(() => {
    if (diamondFilterState && diamondFilterState.page !== undefined) {
      setCurrentPage(diamondFilterState.page)
    }
  }, [diamondFilterState, diamondFilterState?.page])

  //Dynamically update pagination limits
  const updatePageLimits = useCallback(() => {
    const maxLimit = Math.ceil(currentPage / pageNumberLimit) * pageNumberLimit
    const minLimit = maxLimit - pageNumberLimit + 1

    setMaxPageNumberLimit(maxLimit)
    setMinPageNumberLimit(minLimit)
  }, [currentPage, pageNumberLimit])

  useEffect(() => {
    if (diamondMeta) {
      setTotalPages(diamondMeta)
    } else if (settingTotalPages) {
      setTotalPages(settingTotalPages)
    }
    updatePageLimits()
  }, [
    currentPage,
    diamondMeta,
    pageNumberLimit,
    updatePageLimits,
    settingTotalPages,
  ])

  const goToTop = (top: number) => {
    window.scrollTo({
      top: top,
      behavior: 'smooth',
    })
  }
  const handleClick = (id: number) => {
    goToTop(top)
    setTimeout(() => {
      setCurrentPage(id)
    }, 300)
  }
  const handleFirst = () => {
    goToTop(top)
    setTimeout(() => {
      setCurrentPage(1)
      setMaxPageNumberLimit(5)
      setMinPageNumberLimit(0)
    }, 300)
  }

  const handleLast = () => {
    goToTop(top)
    const lastPage = totalPages
    setTimeout(() => {
      setCurrentPage(lastPage)
      setMaxPageNumberLimit(lastPage)
      setMinPageNumberLimit(lastPage - 5)
    }, 300)
  }
  const handleNext = () => {
    goToTop(top)
    setTimeout(() => {
      setCurrentPage(currentPage + 1)
      if (currentPage + 1 > maxPageNumberLimit) {
        setMaxPageNumberLimit(maxPageNumberLimit + pageNumberLimit)
        setMinPageNumberLimit(minPageNumberLimit + pageNumberLimit)
      }
    }, 300)
  }

  const handlePrev = () => {
    goToTop(top)
    setTimeout(() => {
      setCurrentPage(currentPage - 1)
      if (currentPage - 1 === minPageNumberLimit) {
        setMaxPageNumberLimit(maxPageNumberLimit - pageNumberLimit)
        setMinPageNumberLimit(minPageNumberLimit - pageNumberLimit)
      }
    }, 300)
  }

  const pages = Array.from({ length: totalPages }, (_, index) => index + 1)

  const renderPageNumbers = pages.map((number) => {
    if (number >= minPageNumberLimit && number <= maxPageNumberLimit) {
      return (
        <li
          key={number}
          id={String(number)}
          className={cn([
            currentPage === number ? s.pageNumberActive : s.pageNumber,
          ])}
          onClick={() => handleClick(number)}
        >
          {number}
        </li>
      )
    } else {
      return null
    }
  })
  let pageIncrementBtn = null
  if (
    currentPage <= maxPageNumberLimit &&
    currentPage !== totalPages &&
    totalPages > 1
  ) {
    pageIncrementBtn = (
      <>
        <li
          className={cn([
            totalPages < 5 || totalPages - currentPage < 5
              ? 'hidden'
              : 'flex border border-gray-200',
          ])}
        >
          <button className={s.btnHellip} disabled>
            &hellip;
          </button>
        </li>
        <li onClick={handleNext} className={s.buttonPage}>
          <button className={s.btn} disabled={currentPage === totalPages}>
            <FontAwesomeIcon
              icon={faAngleDoubleRight}
              className="text-xs text-accent-9 p-2"
            />
          </button>
        </li>
      </>
    )
  }
  let pageDecrementBtn = null
  if (currentPage > 1 && totalPages > 1) {
    pageDecrementBtn = (
      <>
        <li onClick={handlePrev} className={s.buttonPage}>
          <button className={s.btn}>
            <FontAwesomeIcon
              icon={faAngleDoubleLeft}
              className="text-xs text-accent-9 p-2"
            />
          </button>
        </li>
        <li
          className={cn([
            totalPages < 5 || currentPage < 6
              ? 'hidden'
              : 'flex border border-gray-200',
          ])}
        >
          <button className={s.btnHellip} disabled>
            &hellip;
          </button>
        </li>
      </>
    )
  }
  if (totalPages === 1) {
    return null
  }
  return (
    <div>
      <ul className="flex flex-wrap justify-center gap-1 h-auto md:h-7 sm:flex-nowrap">
        {currentPage !== 1 && (
          <li
            className={cn({
              [s.buttonPageDisable]: currentPage === 1,
              [s.buttonPage]: currentPage !== 1,
            })}
          >
            <button
              className={s.btn}
              onClick={handleFirst}
              disabled={currentPage === 1}
            >
              <FontAwesomeIcon
                icon={faAngleDoubleLeft}
                className="text-xs text-accent-9"
              />
              First
            </button>
          </li>
        )}
        {pageDecrementBtn}
        {renderPageNumbers}
        {pageIncrementBtn}
        {currentPage !== totalPages && (
          <li
            className={cn({
              [s.buttonPageDisable]: currentPage === totalPages,
              [s.buttonPage]: currentPage !== totalPages,
            })}
          >
            <button
              className={s.btn}
              onClick={handleLast}
              disabled={currentPage === totalPages}
            >
              Last
              <FontAwesomeIcon
                icon={faAngleDoubleRight}
                className="text-xs text-accent-9"
              />
            </button>
          </li>
        )}
      </ul>
    </div>
  )
}

export default Pagination
